import { useCallback, useMemo, useState } from "react";
import type { McpResource } from "./ResourceSelector";

interface UseResourceManagementOptions {
  resources: McpResource[] | null;
  error: Error | null;
}

interface UseResourceManagementResult {
  selectedResource: string;
  selectedResourceData: McpResource | null;
  onResourceSelect: (uri: string) => void;
  expandedSections: Record<string, boolean>;
  onToggleSection: (section: string) => void;
  resourcesNotSupported: boolean;
}

export function useResourceManagement({
  resources,
  error,
}: UseResourceManagementOptions): UseResourceManagementResult {
  const [selectedResource, setSelectedResource] = useState<string>("");
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    resources: true,
  });

  const onResourceSelect = useCallback((uri: string) => {
    setSelectedResource(uri);
  }, []);

  const onToggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  const selectedResourceData = useMemo(() => {
    if (!selectedResource || !resources) {
      return null;
    }
    return (
      resources.find((resource) => resource.uri === selectedResource) || null
    );
  }, [selectedResource, resources]);

  const resourcesNotSupported = useMemo(() => {
    if (!error) {
      return false;
    }

    // Check if the error indicates resources are not supported
    // This matches the logic from ProjectInspectorTab
    if (
      error.name === "FpApiError" &&
      "details" in error &&
      typeof error.details === "string"
    ) {
      try {
        const info = JSON.parse(error.details) as {
          error?: {
            details?: {
              data?: { error?: { code?: number; message?: string } };
            };
          };
        };
        const code = info?.error?.details?.data?.error?.code;
        const message = info?.error?.details?.data?.error?.message;
        return code === -32601 || message === "Method not found";
      } catch {
        return false;
      }
    }
    return false;
  }, [error]);

  return {
    selectedResource,
    selectedResourceData,
    onResourceSelect,
    expandedSections,
    onToggleSection,
    resourcesNotSupported,
  };
}
